import React, { useState } from 'react';
import { Tooltip } from '~/components/ui/Tooltip';
import { copyToClipboard } from '~/lib/utils/netlify-helpers';
import type { DeployConfirmationProps } from '~/lib/types/netlify';

export const DeployConfirmation = ({
  hasExistingDeployment,
  previousDeployUrl,
  deploymentStatus,
  onRedeploy: onDeploy,
}: DeployConfirmationProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!previousDeployUrl) {
      return;
    }

    const success = await copyToClipboard(previousDeployUrl);

    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getButtonLabel = () => {
    if (deploymentStatus === 'error') {
      return 'Try Again';
    } else if (deploymentStatus === 'ready' || hasExistingDeployment) {
      return 'Redeploy';
    } else {
      return 'Confirm Deployment';
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-[#e7e7e7]/70 text-sm m-0">
        {hasExistingDeployment && previousDeployUrl && deploymentStatus !== 'ready' ? (
          <div>
            <span className="pb-3">This project was previously deployed to </span>
            <div className="flex gap-2 mt-3">
              <a
                href={previousDeployUrl ?? ''}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-between gap-2 px-4 py-3 rounded-lg 
                  border border-white/10 bg-white/5 hover:bg-white/10 
                  transition-all duration-200 group"
              >
                <span
                  className="text-sm text-[#e7e7e7] group-hover:text-white 
                  transition-colors truncate"
                >
                  {previousDeployUrl.slice(0, 40)}
                </span>
                <div
                  className="i-ph:arrow-square-out w-4 h-4 text-[#e7e7e7]/60 
                  group-hover:text-white transition-colors flex-shrink-0"
                />
              </a>
              <Tooltip content={copied ? 'Copied!' : 'Copy'}>
                <button
                  onClick={handleCopy}
                  className="p-3 rounded-lg border border-white/10 bg-white/5 
                    hover:bg-white/10 transition-all duration-200
                    text-[#e7e7e7]/60 hover:text-white"
                >
                  <div className={copied ? 'i-ph:check w-4 h-4' : 'i-ph:copy w-4 h-4'} />
                </button>
              </Tooltip>
            </div>
          </div>
        ) : (
          <span>Are you sure you want to deploy your site to Netlify?</span>
        )}
      </p>
      <button
        onClick={onDeploy}
        className="w-full px-6 py-2.5 bg-gradient-to-b from-alpha-primary-start to-alpha-primary-end 
          rounded-lg text-white text-sm font-medium hover:brightness-94 transition-all 
          disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={deploymentStatus === 'new' || deploymentStatus === 'building'}
      >
        {getButtonLabel()}
      </button>
    </div>
  );
};
